import dayjs from 'dayjs';
import { t } from 'i18next';
import { makeAutoObservable } from 'mobx';
import { MouseEvent } from 'react';
import { HolidayType } from '../../components/Holidays/graphql';
import { RoomsType } from '../../components/Rooms/graphql/types';
import { SchoolYearWithoutHolidayType } from '../../components/SchoolYear/graphql/types';
import { ForeignTripCard, ForeignTripIndex } from '../../components/TeachingBlockGrid/foreignTripIndex';
import {
  CardAutoFitOptions,
  CardDurationModalType,
  CardResizeRange,
  CardRoomsModalType,
  ConflictModalType,
  GridContext,
  GridDateType,
  GridHighlightType,
  GridHolidayType,
  GridWeekType,
  Item,
  PlacedTeachingBlockCardType,
  ResizeEdge,
  RowItem,
  TeachingBlockBoardVersion,
  TeachingBlockCardType,
  TimeConflictModalType,
  Warnings,
} from '../../components/TeachingBlockGrid/types';
import { availabilityStoreHandler } from '../../pages/Timetable/Plan/TeachingBlockVersion/TeachingBlockVersion';
import { _ClassesQuery, _PeopleQuery, _RoomsQuery, _SubjectsQuery } from '../../types/planung-graphql-client-defs';
import { getDaysDifference, isFirstBeforeSecond } from '../../utils/dateCalculations';
import { TeachingBlockDayAvailabilityStore } from '../DayAvailabilityStore';
import type { BoardLayout } from '../TimetableVersion/PinboardStoreTypes';
import type { UserConfigStore } from '../userConfig/UserConfigStore';
import { TeachingBlockCardStore } from './TeachingBlockCardStore';
import { TeachingBlockConflictStore } from './TeachingBlockConflictStore';
import { TeachingBlockUIStore } from './TeachingBlockUIStore';

export type TeachingBlockStoreProps = {
  schoolYear: SchoolYearWithoutHolidayType;
  holidays: HolidayType[];
  rooms: RoomsType[];
  initialCards: TeachingBlockCardType[];
  foreignTripIndex: ForeignTripIndex;
  currentVersion: TeachingBlockBoardVersion;
  calculateConflicts: boolean;
  readonly: boolean;
  temporarilyEditable: boolean;
  /** Klassen-Gruppen aus den Platzhalter-Lessons des SubjectContainers (eine Set pro Lesson). */
  placeholderLessonGroups: Set<string>[];
  /**
   * Quelle und Ziel der gespeicherten Ansichtseinstellungen. Wird hereingereicht
   * statt importiert (kein Zirkelbezug); ohne sie bleibt alles im Speicher.
   */
  userConfig?: UserConfigStore | null;
};

/**
 * Fassade der TeachingBlock-Planung (Phase 7.1, an PinboardStore orientiert).
 * Hält Grid-State (Schuljahr/Dates/Weeks/Monate/Ferien) sowie – vorübergehend bis
 * Phase 7.3 – den UI-State (pickedCard, Selektion, Modals). Die Kartendaten,
 * Platzierung, Räume und das Ferien-Splitting liegen im `cardStore` und werden
 * hier nur delegiert, damit die öffentliche Store-API für die Grid-Komponenten
 * unverändert bleibt.
 */
export class TeachingBlockStore {
  public readonly cardStore: TeachingBlockCardStore;
  public readonly conflictStore: TeachingBlockConflictStore;
  public readonly uiStore: TeachingBlockUIStore;

  public schoolYear: SchoolYearWithoutHolidayType;
  /** intern – über `rawHolidays` lesen */
  _holidays: HolidayType[];
  public holidays: Map<string, GridHolidayType> = new Map();
  /** intern – über `rawRooms` lesen */
  _rooms: RoomsType[];
  public currentVersion: TeachingBlockBoardVersion;

  public readonly: boolean = false;

  /**
   * „Temporär editierbar": eine aktive, gesperrte Version wird gerade korrigiert
   * (analog PinboardStore). Steuert zusammen mit `readonly`, ob das Board
   * bearbeitet werden darf. Wird über `setTemporarilyEditable` umgeschaltet.
   */
  public temporarilyEditable: boolean = false;

  /**
   * Schalter „Konflikte berechnen" (Menü `GridMenuBar`). Vorgesehen als Gate für
   * die Konflikt-Kopplung Stundenplan↔Epoche (Refactoring 7.4, Abschnitt 3): ist
   * er aktiv, soll der `TeachingBlockConflictStore` seinen Platzhalter-Filter
   * (`_placeholderConflictFilter`) aufbauen. Wird beim Toggle über die
   * Store-Neukonstruktion (useMemo-Dep in `TeachingBlockContext`) aktualisiert.
   * TODO(7.x): noch nicht ans Konfliktverhalten gekoppelt – aktuell unverändert.
   */
  public calculateConflicts: boolean = true;

  public placeholderLessonGroups: Set<string>[] = [];

  public months: number[] = [];
  public dates: Map<string, GridDateType> = new Map();
  public weeks: Map<string, GridDateType[]> = new Map();

  /** intern – über `getAvailabilityStore()` lesen */
  _availabilityStore: TeachingBlockDayAvailabilityStore | null;

  /** intern – über `foreignTripsForRowWeek()` lesen */
  _foreignTripIndex: ForeignTripIndex;

  constructor({
    schoolYear,
    holidays,
    rooms,
    initialCards,
    foreignTripIndex,
    currentVersion,
    calculateConflicts,
    readonly,
    temporarilyEditable,
    placeholderLessonGroups,
    userConfig = null,
  }: TeachingBlockStoreProps) {
    this.readonly = readonly;
    this.temporarilyEditable = temporarilyEditable;
    this.schoolYear = schoolYear;
    this._holidays = holidays;
    this._rooms = rooms;
    this.currentVersion = currentVersion;
    this.calculateConflicts = calculateConflicts;
    this.placeholderLessonGroups = placeholderLessonGroups;
    this._availabilityStore = availabilityStoreHandler.get(currentVersion.uuid);
    this._foreignTripIndex = foreignTripIndex;

    this.conflictStore = new TeachingBlockConflictStore(this);
    this.uiStore = new TeachingBlockUIStore(this, userConfig);
    this.cardStore = new TeachingBlockCardStore(this, initialCards);

    this._initGrid(holidays);
    this.cardStore._loadCards();
    // Erst nach dem Laden: `_loadCards` setzt den Kontext selbst.
    this.uiStore.restorePersistedContext();

    makeAutoObservable(this, {
      cardStore: false,
      conflictStore: false,
      uiStore: false,
      _availabilityStore: false,
      _foreignTripIndex: false,
      _holidays: false,
      _rooms: false,
    });
  }

  // ─── Zugriff für Sub-Stores ───────────────────────────────────────────────

  public getAvailabilityStore() {
    return this._availabilityStore;
  }

  /** Fremde Klassenfahrten dieser Zeile und Woche. Reiner Map-Zugriff, kein MobX. */
  public foreignTripsForRowWeek(indexKey: string): ForeignTripCard[] | undefined {
    return this._foreignTripIndex.get(indexKey);
  }

  /** Korrektur-Modus umschalten: entsperrt/sperrt das Board (analog PinboardStore). */
  public setTemporarilyEditable(value: boolean) {
    this.temporarilyEditable = value;
    this.readonly = !value;
  }

  /**
   * Wird aus TeachingBlockContext aufgerufen, sobald die SubjectContainer-
   * PlaceholderLessons-Query aufgelöst ist (StoreHandler cached den Store, daher
   * kein Neuaufbau des Stores möglich).
   */
  public setPlaceholderLessonGroups(groups: Set<string>[]) {
    this.placeholderLessonGroups = groups;
    if (this.calculateConflicts) {
      this.conflictStore.setConflicts();
    }
  }

  public get rawHolidays(): HolidayType[] {
    return this._holidays;
  }

  public get rawRooms(): RoomsType[] {
    return this._rooms;
  }

  // ─── Delegationen an cardStore (State) ────────────────────────────────────

  public get freeCards() {
    return this.cardStore.freeCards;
  }
  public get placedCards() {
    return this.cardStore.placedCards;
  }
  /** Platzierte Karten inkl. der als Ferien-Segmente gesplitteten Eltern (s. cardStore). */
  public get allPlacedCards() {
    return this.cardStore.allPlacedCards;
  }
  public get virtualCards() {
    return this.cardStore.virtualCards;
  }
  public get placedCardsByRowWeek() {
    return this.cardStore.placedCardsByRowWeek;
  }
  public get virtualCardsByRowWeek() {
    return this.cardStore.virtualCardsByRowWeek;
  }
  /** Stapel-Layout einer Wahlfach-Karte, oder `undefined` wenn sie nicht gestapelt ist. */
  public getCoexistLayout(rowValue: string, cardUuid: string) {
    return this.cardStore.getCoexistLayout(rowValue, cardUuid);
  }
  public get rows(): RowItem[] {
    return this.cardStore.getRows();
  }
  public get cardRooms() {
    return this.cardStore.cardRooms;
  }
  public get teachers() {
    return this.cardStore.teachers;
  }
  public get classes() {
    return this.cardStore.classes;
  }
  public get subjects() {
    return this.cardStore.subjects;
  }
  public get rooms() {
    return this.cardStore.rooms;
  }
  public get availableCards() {
    return this.cardStore.availableCards;
  }
  public get cardsWithWarnings() {
    return this.placedCards.filter((card) => this.conflictStore.cardConflicts.has(card.uuid));
  }
  public get selectedRowCards() {
    return this.cardStore.selectedRowCards;
  }

  // ─── Delegationen an conflictStore ────────────────────────────────────────

  public get cardConflicts() {
    return this.conflictStore.cardConflicts;
  }
  public setConflicts() {
    return this.conflictStore.setConflicts();
  }
  public updateConflictsForCards(cardUuids: Set<string>) {
    return this.conflictStore.updateConflictsForCards(cardUuids);
  }

  /**
   * Anzeige-Warnungen einer Karte aus `cardConflicts` ableiten (ersetzt das
   * frühere `card.warnings`). Bei gesplitteten Karten unter der Parent-UUID.
   */
  public getWarningsForCard(uuid: string): Warnings {
    const availability = this.conflictStore.cardConflicts.get(uuid);
    if (!availability) return [];
    return Array.from(availability.entries()).map(([resourceUuid, conflict]) =>
      this.cardStore.getWarning(uuid, conflict, resourceUuid),
    );
  }

  // ─── Delegationen an cardStore (Methoden) ─────────────────────────────────

  public loadData(params: {
    teachers: Pick<_PeopleQuery, 'people'>['people'];
    classes: Pick<_ClassesQuery, 'classes'>['classes'];
    rooms: Pick<_RoomsQuery, 'rooms'>['rooms'];
    subjects: Pick<_SubjectsQuery, 'subjects'>['subjects'];
  }) {
    return this.cardStore.loadData(params);
  }
  public addClasses(classes: Pick<_ClassesQuery, 'classes'>['classes']) {
    return this.cardStore.addClasses(classes);
  }
  public removeClasses(classesUuids: string[]) {
    return this.cardStore.removeClasses(classesUuids);
  }
  public removeTeachers(teacherUuids: string[]) {
    return this.cardStore.removeTeachers(teacherUuids);
  }
  public removeSubjects(subjectUuids: string[]) {
    return this.cardStore.removeSubjects(subjectUuids);
  }
  public removeRooms(roomUuids: string[]) {
    return this.cardStore.removeRooms(roomUuids);
  }
  public mapRoomsForCard(card: TeachingBlockCardType) {
    return this.cardStore.mapRoomsForCard(card);
  }
  public setRoomsForCards(
    cards: Array<PlacedTeachingBlockCardType | TeachingBlockCardType>,
    roomsToSet: Array<{ __typename?: 'Room'; name: string; uuid: string }>,
    keepCurrent: boolean = false,
  ) {
    return this.cardStore.setRoomsForCards(cards, roomsToSet, keepCurrent);
  }
  public removeRoomsFromCards(
    cards: Array<PlacedTeachingBlockCardType | TeachingBlockCardType>,
    roomsToRemove: Array<{ __typename?: 'Room'; name: string; uuid: string }>,
    removeAll: boolean = false,
  ) {
    return this.cardStore.removeRoomsFromCards(cards, roomsToRemove, removeAll);
  }
  public isUuidPresentInTimetableCard(card: TeachingBlockCardType, uuid: string): boolean {
    return this.cardStore.isUuidPresentInTimetableCard(card, uuid);
  }
  public isUuidPresentInVirtualCard(...args: Parameters<TeachingBlockCardStore['isUuidPresentInVirtualCard']>) {
    return this.cardStore.isUuidPresentInVirtualCard(...args);
  }
  public getParentCard(uuid: string) {
    return this.cardStore.getParentCard(uuid);
  }
  public getCardsInRow(row: RowItem | Item): TeachingBlockCardType[] {
    return this.cardStore.getCardsInRow(row);
  }
  public getAvailabilityCardForPickedCard() {
    return this.cardStore.getAvailabilityCardForPickedCard();
  }
  public lockCard(card: TeachingBlockCardType) {
    return this.cardStore.lockCard(card);
  }
  public unlockCard(card: TeachingBlockCardType) {
    return this.cardStore.unlockCard(card);
  }
  public discardCard(card: TeachingBlockCardType | PlacedTeachingBlockCardType) {
    return this.cardStore.discardCard(card);
  }
  public placeCard(
    card: TeachingBlockCardType,
    startDateString: string,
    pos?: { x: number; y: number },
    ignoreConflict?: boolean,
  ) {
    return this.cardStore.placeCard(card, startDateString, pos, ignoreConflict);
  }
  public updateCard(
    card: TeachingBlockCardType,
    startDateString: string,
    endDateString: string,
    includeHoliday = false,
    virtual = false,
  ) {
    return this.cardStore.updateCard(card, startDateString, endDateString, includeHoliday, virtual);
  }
  public splitTimeConflict(card?: TeachingBlockCardType, startDateString?: string, save = true) {
    return this.cardStore.splitTimeConflict(card, startDateString, save);
  }
  public getCardLengthDeviationDays(card: TeachingBlockCardType) {
    return this.cardStore.getCardLengthDeviationDays(card);
  }
  public canAdjustCard(card: TeachingBlockCardType | PlacedTeachingBlockCardType) {
    return this.uiStore.canAdjustCard(card);
  }

  // ─── Grid-Setup ───────────────────────────────────────────────────────────

  private _initGrid(holidaysData: HolidayType[]) {
    this.months = [];
    this.dates = new Map();
    this.weeks = new Map();
    this.holidays = new Map();

    const normalizedHolidays = holidaysData
      .map((holiday) => ({
        __typename: holiday.__typename,
        uuid: holiday.uuid,
        name: holiday.name,
        start: dayjs(holiday.start).startOf('day').format('YYYY-MM-DD'),
        end: dayjs(holiday.end).startOf('day').format('YYYY-MM-DD'),
      }))
      .sort((a, b) => (isFirstBeforeSecond(a.start, b.start) ? -1 : 1));

    const displayStart = this._getDisplayStart(normalizedHolidays);
    const displayEnd = displayStart.add(11, 'month').endOf('month').startOf('day');

    const holidaysWithSchoolYearEnd = [...normalizedHolidays];
    const schoolYearEndIndicatorStart = dayjs(this.schoolYear.end).add(1, 'day').startOf('day');
    if (schoolYearEndIndicatorStart.isSameOrBefore(displayEnd, 'day')) {
      holidaysWithSchoolYearEnd.push({
        __typename: 'HolidayItem',
        uuid: 'school-year-end-indicator',
        name: t('pinboard.edit.schoolYearEndHint', { year: this.schoolYear?.shortName }),
        start: schoolYearEndIndicatorStart.format('YYYY-MM-DD'),
        end: displayEnd.format('YYYY-MM-DD'),
      });
    }

    holidaysWithSchoolYearEnd
      .sort((a, b) => (isFirstBeforeSecond(a.start, b.start) ? -1 : 1))
      .forEach((holiday) =>
        this.holidays.set(holiday.uuid, {
          uuid: holiday.uuid,
          name: holiday.name,
          start: holiday.start,
          end: holiday.end,
        }),
      );

    for (let i = 0; i < 12; i++) {
      this.months.push(displayStart.add(i, 'month').month() + 1);
    }

    const gridStart = displayStart.startOf('month').startOf('isoWeek').startOf('day');
    const gridEnd = displayEnd.endOf('month').endOf('isoWeek').startOf('day');
    const dayCount = getDaysDifference(gridStart, gridEnd);

    for (let i = 0; i < dayCount; i++) {
      const date = gridStart.add(i, 'day');
      const dateString = date.format('YYYY-MM-DD');
      const holiday = [...this.holidays.values()].find(
        (mappedHoliday) =>
          date.isSameOrAfter(mappedHoliday.start, 'day') && date.isSameOrBefore(mappedHoliday.end, 'day'),
      );

      this.dates.set(dateString, {
        // ISO-Kalenderwoche (KW): locale-unabhängig, Montag-basiert und liefert –
        // anders als das `weekOfYear`-Plugin (`.week()`) – in Jahren mit 53 Wochen
        // korrekt die KW 53, statt den Jahreswechsel fälschlich auf „1" zu rollen.
        weekNumber: date.isoWeek(),
        dateString,
        holidayUuid: holiday?.uuid ?? null,
      });
    }

    const datesArray = Array.from(this.dates.keys());
    for (let x = 0; x < this.dates.size; x += 7) {
      const daysInWeek: GridDateType[] = [];
      for (let y = 0; y < 7; y++) {
        const dayString = datesArray[x + y];
        const day = this.dates.get(dayString);
        if (!day) throw new Error('day not found');
        daysInWeek.push(day);
      }
      const weekString = `${daysInWeek[0].weekNumber}_${datesArray[x]}`;
      this.weeks.set(weekString, daysInWeek);
    }
  }

  private _getDisplayStart(holidays: Array<Pick<HolidayType, 'end' | 'start'>>): dayjs.Dayjs {
    const schoolYearStart = dayjs(this.schoolYear.start).startOf('day');
    const augustStart = schoolYearStart.month(7).startOf('month');

    return this._isFullyCoveredByHolidays(augustStart, holidays) ? augustStart.add(1, 'month') : augustStart;
  }

  private _isFullyCoveredByHolidays(
    monthStart: dayjs.Dayjs,
    holidays: Array<Pick<HolidayType, 'end' | 'start'>>,
  ): boolean {
    const monthEnd = monthStart.endOf('month').startOf('day');
    const dayCount = getDaysDifference(monthStart, monthEnd);

    for (let i = 0; i < dayCount; i++) {
      const day = monthStart.add(i, 'day');
      const isHoliday = holidays.some(
        (holiday) =>
          day.isSameOrAfter(dayjs(holiday.start).startOf('day'), 'day') &&
          day.isSameOrBefore(dayjs(holiday.end).startOf('day'), 'day'),
      );

      if (!isHoliday) {
        return false;
      }
    }

    return true;
  }

  // ─── Delegationen an uiStore (State) ──────────────────────────────────────

  public get gridContext() {
    return this.uiStore.gridContext;
  }
  public get groupRoomsByBuilding() {
    return this.uiStore.groupRoomsByBuilding;
  }
  public setGroupRoomsByBuilding(value: boolean) {
    return this.uiStore.setGroupRoomsByBuilding(value);
  }
  public get showTeacherInsteadOfSubject() {
    return this.uiStore.showTeacherInsteadOfSubject;
  }
  public setShowTeacherInsteadOfSubject(value: boolean) {
    return this.uiStore.setShowTeacherInsteadOfSubject(value);
  }
  public get cardColorBySubject() {
    return this.uiStore.cardColorBySubject;
  }
  public setCardColorBySubject(value: boolean) {
    return this.uiStore.setCardColorBySubject(value);
  }
  public get selectedCard() {
    return this.uiStore.selectedCard;
  }
  public get pickedCard() {
    return this.uiStore.pickedCard;
  }
  public get pinnedCard() {
    return this.uiStore.pinnedCard;
  }
  public get selectedRows() {
    return this.uiStore.selectedRows;
  }
  public get conflictModal() {
    return this.uiStore.conflictModal;
  }
  public get timeConflictModal() {
    return this.uiStore.timeConflictModal;
  }
  /** cardStore schreibt direkt über `setTimeConflictModal`; Setter für Altpfade */
  public set timeConflictModal(modal: TimeConflictModalType) {
    this.uiStore.timeConflictModal = modal;
  }
  public get cardDurationModal() {
    return this.uiStore.cardDurationModal;
  }
  public get cardRoomsModal() {
    return this.uiStore.cardRoomsModal;
  }
  public get highlightMode() {
    return this.uiStore.highlightMode;
  }
  public get rowsToHighlight() {
    return this.uiStore.rowsToHighlight;
  }
  public get hiddenRows() {
    return this.uiStore.hiddenRows;
  }
  public get rowHeight() {
    return this.uiStore.rowHeight;
  }
  public get isFullscreen() {
    return this.uiStore.isFullscreen;
  }
  public get cardSearchString() {
    return this.uiStore.cardSearchString;
  }
  public get resizingCard() {
    return this.uiStore.resizingCard;
  }
  public get boardLayout() {
    return this.uiStore.boardLayout;
  }
  public get boardTopHeight() {
    return this.uiStore.boardTopHeight;
  }
  public get boardSplitWidth() {
    return this.uiStore.boardSplitWidth;
  }
  public get boardPickerWidth() {
    return this.uiStore.boardPickerWidth;
  }
  public get boardPickerHeight() {
    return this.uiStore.boardPickerHeight;
  }
  public get showPreviewMap() {
    return this.uiStore.showPreviewMap;
  }
  public get previewMapContext() {
    return this.uiStore.previewMapContext;
  }
  public get previewMapAuto() {
    return this.uiStore.previewMapAuto;
  }
  public get boardPreviewMapWidth() {
    return this.uiStore.boardPreviewMapWidth;
  }
  public get boardPreviewMapHeight() {
    return this.uiStore.boardPreviewMapHeight;
  }

  // ─── Delegationen an uiStore (Methoden) ───────────────────────────────────

  public setBoardLayout(layout: BoardLayout) {
    return this.uiStore.setBoardLayout(layout);
  }
  public toggleBoardLayout() {
    return this.uiStore.toggleBoardLayout();
  }
  public setBoardTopHeight(height: number) {
    return this.uiStore.setBoardTopHeight(height);
  }
  public setBoardSplitWidth(width: number) {
    return this.uiStore.setBoardSplitWidth(width);
  }
  public setBoardPickerWidth(width: number) {
    return this.uiStore.setBoardPickerWidth(width);
  }
  public setBoardPickerHeight(height: number) {
    return this.uiStore.setBoardPickerHeight(height);
  }
  public setShowPreviewMap(show: boolean) {
    return this.uiStore.setShowPreviewMap(show);
  }
  public toggleShowPreviewMap() {
    return this.uiStore.toggleShowPreviewMap();
  }
  public setPreviewMapContext(context: GridContext) {
    return this.uiStore.setPreviewMapContext(context);
  }
  public setPreviewMapAuto(auto: boolean) {
    return this.uiStore.setPreviewMapAuto(auto);
  }
  public setBoardPreviewMapWidth(width: number) {
    return this.uiStore.setBoardPreviewMapWidth(width);
  }
  public setBoardPreviewMapHeight(height: number) {
    return this.uiStore.setBoardPreviewMapHeight(height);
  }

  public setContext(type: GridContext) {
    return this.uiStore.setContext(type);
  }

  /** Bewusster Kontextwechsel (wird gespeichert), siehe `TeachingBlockUIStore.selectContext`. */
  public selectContext(type: GridContext) {
    return this.uiStore.selectContext(type);
  }

  /** Beendet die Beobachter des Stores (wird beim Verwerfen des Stores aufgerufen). */
  public dispose() {
    this.uiStore.dispose();
  }
  public selectCard(card: TeachingBlockCardType | null) {
    return this.uiStore.selectCard(card);
  }
  public onEscape(card: PlacedTeachingBlockCardType | TeachingBlockCardType) {
    return this.uiStore.onEscape(card);
  }
  public pickCard(
    card: TeachingBlockCardType | PlacedTeachingBlockCardType | null,
    position?: { x: number; y: number },
    fromStack?: boolean,
    fromRoomUuid?: string,
    isCopy?: boolean,
  ) {
    return this.uiStore.pickCard(card, position, fromStack, fromRoomUuid, isCopy);
  }
  public get pickedFromRoom() {
    return this.uiStore.pickedFromRoom;
  }
  public get pickIsCopy() {
    return this.uiStore.pickIsCopy;
  }
  public pinCard(card: TeachingBlockCardType) {
    return this.uiStore.pinCard(card);
  }
  public unpinCard() {
    return this.uiStore.unpinCard();
  }
  public selectRows(rows: RowItem[]) {
    return this.uiStore.selectRows(rows);
  }
  public rowClickHandler(e: MouseEvent<HTMLDivElement>, row: RowItem) {
    return this.uiStore.rowClickHandler(e, row);
  }
  public lockAllCards() {
    return this.uiStore.lockAllCards();
  }
  public unlockAllCards() {
    return this.uiStore.unlockAllCards();
  }
  public discardAllCards() {
    return this.uiStore.discardAllCards();
  }
  public lockCardsInSelectedRows() {
    return this.uiStore.lockCardsInSelectedRows();
  }
  public lockCardsInRow(row: RowItem) {
    return this.uiStore.lockCardsInRow(row);
  }
  public unlockCardsInSelectedRows() {
    return this.uiStore.unlockCardsInSelectedRows();
  }
  public unlockCardsInRow(row: RowItem) {
    return this.uiStore.unlockCardsInRow(row);
  }
  public discardCardsInSelectedRows() {
    return this.uiStore.discardCardsInSelectedRows();
  }
  public discardCardsInRow(row: RowItem) {
    return this.uiStore.discardCardsInRow(row);
  }
  public assignRoomToPickedCard(row: RowItem) {
    return this.uiStore.assignRoomToPickedCard(row);
  }
  public removePickedRoomToStack() {
    return this.uiStore.removePickedRoomToStack();
  }
  public ignoreConflict() {
    return this.uiStore.ignoreConflict();
  }
  public abortConflict() {
    return this.uiStore.abortConflict();
  }
  public ignoreTimeConflict() {
    return this.uiStore.ignoreTimeConflict();
  }
  public abortTimeConflict() {
    return this.uiStore.abortTimeConflict();
  }
  public resetTimeConflict() {
    return this.uiStore.resetTimeConflict();
  }
  public reset() {
    return this.uiStore.reset();
  }
  public setConflictModal(modal: ConflictModalType) {
    return this.uiStore.setConflictModal(modal);
  }
  public setTimeConflictModal(modal: TimeConflictModalType) {
    return this.uiStore.setTimeConflictModal(modal);
  }
  public setCardDurationModal(modal: CardDurationModalType) {
    return this.uiStore.setCardDurationModal(modal);
  }
  public getCardResizeBounds(
    card: TeachingBlockCardType | PlacedTeachingBlockCardType,
    edge: ResizeEdge,
    range?: CardResizeRange,
    crossHolidays = false,
  ) {
    return this.uiStore.getCardResizeBounds(card, edge, range, crossHolidays);
  }
  public resizeCardEdge(
    card: TeachingBlockCardType | PlacedTeachingBlockCardType,
    edge: ResizeEdge,
    targetDateString: string,
    range?: CardResizeRange,
    isVirtual?: boolean,
  ) {
    return this.uiStore.resizeCardEdge(card, edge, targetDateString, range, isVirtual);
  }
  public autoFitCardToGap(card: TeachingBlockCardType | PlacedTeachingBlockCardType, options?: CardAutoFitOptions) {
    return this.uiStore.autoFitCardToGap(card, options);
  }
  public setCardRoomsModal(modal: CardRoomsModalType) {
    return this.uiStore.setCardRoomsModal(modal);
  }
  public setHighlightMode(mode: GridHighlightType) {
    return this.uiStore.setHighlightMode(mode);
  }
  public toggleRowsVisibility(rows: RowItem[]) {
    return this.uiStore.toggleRowsVisibility(rows);
  }
  public showAllRows() {
    return this.uiStore.showAllRows();
  }
  public setRowHeight(height: number) {
    return this.uiStore.setRowHeight(height);
  }
  public setDefaultRowHeight() {
    return this.uiStore.setDefaultRowHeight();
  }
  public setIsFullscreen(isFullscreen: boolean) {
    return this.uiStore.setIsFullscreen(isFullscreen);
  }
  public setCardSearchString(value: string) {
    return this.uiStore.setCardSearchString(value);
  }
  public setResizingCard(value: boolean) {
    return this.uiStore.setResizingCard(value);
  }

  // ─── Ferien-Helfer (Grid) ─────────────────────────────────────────────────

  public isFullHoliday(week: GridWeekType | undefined): boolean {
    if (!week) return false;
    return !!week.holiday && week.offsetLeft === 0 && week.offsetRight === 0;
  }

  public isHolidayEnd(week: GridWeekType | undefined): boolean {
    if (!week) return false;
    return !!week.holiday && week.offsetLeft > 0 && week.offsetRight === 0;
  }

  public isHolidayStart(week: GridWeekType | undefined): boolean {
    if (!week) return false;
    return !!week.holiday && week.offsetRight > 0 && week.offsetLeft === 0;
  }
}
